import React from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  InputBase,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LinkedIn,
  YouTube,
  Send as SendIcon,
  Phone,
  Print,
  LocationOn,
  Business,
} from '@mui/icons-material';
import Vision from './Images/Vision.png';
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.dir() === 'rtl';
  const fontFamily = isRTL ? "'Tajawal', sans-serif" : "Arial, sans-serif";

  return (
    <Box
      sx={{
        backgroundColor: '#0f2c2b',
        color: '#fff',
        px: { xs: 3, md: 10 },
        py: { xs: 5, md: 8 },
        fontFamily,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        boxShadow: '0px -4px 15px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}
    >
      <Grid container spacing={5} justifyContent="space-between" alignItems="flex-start" flexDirection={isMobile ? 'column' : 'row'}>
        <Grid item xs={12} md={4}>
          <Typography fontFamily={fontFamily} variant="h6" fontWeight="bold" gutterBottom>
            {i18n.language === 'ar' ? 'ابقَ على اتصال ': 'Stay Connected'}
          </Typography>

          <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
            {[LinkedIn, YouTube].map((Icon, idx) => (
              <IconButton
                key={idx}
                sx={{
                  backgroundColor: '#1a3e3c',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#2a5f5c' },
                  transition: '0.3s',
                }}
              >
                <Icon />
              </IconButton>
            ))}
          </Stack>

          <Box
            component="img"
            src={Vision}
            alt="Vision Logo"
            sx={{
              width: { xs: 100, md: 130 },
              mt: 2,
              filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.4))',
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom fontFamily={fontFamily}>
            {t("subscribeNewsletter")}
          </Typography>

          <Paper
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              window.open('mailto:ame@almansouryah.com', '_blank');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: '30px',
              overflow: 'hidden',
              backgroundColor: '#fff',
              boxShadow: 'inset 1px 1px 3px #d4d4d4, inset -1px -1px 3px #fff',
              height: 40,
              px: 1,
              mt: 1,
              maxWidth: '100%',
            }}
          >
            <InputBase
              placeholder={t("emailAddress")}
              sx={{ flex: 1, ml: 1, fontSize: 14 }}
            />
            <IconButton
              type="submit"
              sx={{
                backgroundColor: 'dodgerblue',
                color: '#fff',
                '&:hover': { backgroundColor: '#1a76d2' },
                p: 1,
                mr: 1,
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Paper>
          <Typography variant="caption" color="#ccc" fontSize="11px" mt={1} fontFamily={fontFamily}>
            {t("privacyNote")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom fontFamily={fontFamily}>
            {t("contactInformation")}
          </Typography>

          <Stack spacing={1.2} mt={1}>
            {[ 
              { icon: Business, text: t("commercialRegistration") },
              { 
                icon: Phone, 
                text: t("telephone"), 
                isPhone: true, 
                direction: 'ltr'  // Force LTR direction for phone number
              },
              { icon: LocationOn, text: t("correspondenceCode") },
            ].map((item, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Box>
                  <item.icon fontSize="small" />
                </Box>
                <Typography
                  variant="body2"
                  fontFamily={fontFamily}
                  sx={{ 
                    whiteSpace: 'nowrap', 
                    direction: item.isPhone ? 'ltr' : 'inherit',  // Apply LTR direction if it's a phone number
                  }}
                >
                  {item.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Box
        mt={5}
        pt={3}
        borderTop="1px solid #1a3e3c"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        fontSize="12px"
        color="#ccc"
      >
        <Typography variant="body2" fontSize="11px" fontFamily={fontFamily}>
          © {new Date().getFullYear()} Al Mansouryah. {t("allRightsReserved")}
        </Typography>
        <Stack direction="row" spacing={2} mt={{ xs: 2, md: 0 }} justifyContent="center">
          <Button sx={{ fontSize: 11, color: '#ccc', fontFamily }}>{t("termsConditions")}</Button>
          <Button sx={{ fontSize: 11, color: '#ccc', fontFamily }}>{t("privacyPolicy")}</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default Footer;
